import { useCallback, useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import http from 'helpers/requests';
import { claimSupplierInvitation, SupplierInvitationApiError } from './invitationApi';
import { destinationForPermissions } from '../App/workspaceNavigation';
import queryClient from '../../queryClient';

const PLAN_RETRY_DELAYS_MS = [0, 100, 250, 500, 1000];

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export async function loadClaimedWorkspaceDestination() {
  let lastError;
  for (const delay of PLAN_RETRY_DELAYS_MS) {
    if (delay) await wait(delay);
    try {
      const response = await http.get('/permissions/me');
      if (response?.data?.plan) return destinationForPermissions(response.data);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('The workspace plan is not ready yet.');
}

export const useSupplierInvitationClaim = (token) => {
  const clerk = useClerk();
  const history = useHistory();
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const claim = useCallback(async () => {
    if (status === 'claiming' || status === 'activating') return;
    setStatus('claiming');
    setError(null);
    try {
      const result = await claimSupplierInvitation(token);
      if (result.status === 'unavailable') {
        setStatus('unavailable');
        return;
      }
      setStatus('activating');
      await clerk.setActive({ organization: result.clerkOrganisationId });
      await http.refreshClerkToken();
      // The invitation route sits above OrgBoundary, so its normal org-switch
      // listener is not mounted here. Remove prior workspace queries before
      // the new workspace renders or the previous org's cached data can leak.
      // Preserve only this page's public inspect query while claim finishes.
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'freeSupplierInvitation',
      });
      const destination = await loadClaimedWorkspaceDestination();
      setStatus('complete');
      history.replace(destination);
    } catch (claimError) {
      const payload =
        claimError instanceof SupplierInvitationApiError
          ? claimError.payload
          : {
              version: 1,
              status: 'retryable_error',
              code: 'client_retry_required',
              message: 'The supplier workspace could not be completed yet.',
            };
      setError(payload);
      setStatus(payload.status);
    }
  }, [clerk, history, status, token]);

  return { claim, status, error };
};
